import type { NavigationEvents } from '../adapters/next-router-adapter'
import { getSessionItem, setSessionItem } from '../storage/safe-storage'

const SS_KEY = 'app:nav:referrer'

export interface PageReloadTracker {
	getStatus(): boolean
}

/**
 * Custom implementation of the native window.referrer
 * Required because the standard window.referrer is not updated when using SPA routing
 * Provides consistent referrer tracking across page reloads and navigation
 */
export class CustomReferrer {
	private inited = false
	private offNavigation?: () => void

	constructor(private navigation?: NavigationEvents, private pageReloadTracker?: PageReloadTracker) {}

	/**
	 * Get current referrer value
	 * @returns referrer string or null if not available
	 */
	get(): string | null {
		return getSessionItem(SS_KEY)
	}

	/**
	 * Initialize referrer tracking
	 * Safe to call multiple times - will only initialize once
	 */
	init(): void {
		if (this.inited || typeof window === 'undefined') return

		this.inited = true

		// Set initial referrer only if page wasn't reloaded
		const wasReloaded = this.pageReloadTracker?.getStatus() ?? false
		if (!wasReloaded) {
			const initialReferrer = document.referrer || ''
			setSessionItem(SS_KEY, initialReferrer)
		}

		// Track navigation changes to update referrer
		if (this.navigation) {
			this.offNavigation = this.navigation.onBeforeHistoryChange(() => {
				setSessionItem(SS_KEY, window.location.href)
			})
		}
	}

	/**
	 * Clean up event listeners
	 */
	destroy(): void {
		if (!this.inited) return

		this.inited = false
		this.offNavigation?.()
	}

	/**
	 * Manually set referrer (useful for testing or special cases)
	 */
	set(referrer: string): void {
		setSessionItem(SS_KEY, referrer)
	}

	/**
	 * Clear referrer
	 */
	clear(): void {
		setSessionItem(SS_KEY, '')
	}
}
